import { useAppData } from "@/context/AppDataContext";
import {
  AgentPipelineModel,
  AllDepartmentsPipelineModel,
  DashboardPipelineModel,
  DepartmentPipelineModel,
} from "@/models/pipeline";
import { useMemo } from "react";
import { DashboardView } from "./DashboardView";
import { TopLevelStatsBox } from "./TopLevelStatsBox";

type PipelineData =
  | { type: "agent"; data: AgentPipelineModel }
  | { type: "department"; data: DepartmentPipelineModel }
  | { type: "all"; data: AllDepartmentsPipelineModel };

interface PipelineStatistics {
  appsTotal?: number;
  warmAppsTotal?: number;
  allocatedOnSelectedDayTotal?: number;
  leadsToday?: number;
  leadsTotal?: number;
  predictedToDocs: number;
  docsToday: number;
  gapDocsOut: number;
  gapReadyToGo: number;
  gapPayout: number;
  gapTotal: number;
  predictedToPay: number;
  paidToday: number;
}

interface SummaryViewProps {
  pipelineData: PipelineData | null;
  statistics: PipelineStatistics | null;
  shouldShowDirectorView: boolean;
  shouldShowHoBView: boolean;
  shouldShowOSMView: boolean;
  shouldShowTeamView: boolean;
  selectedHeadOfBusiness: { id: number; name: string } | null;
  selectedOSM: string | null;
  selectedDepartmentId: string | null;
  hobOverviewData: DashboardPipelineModel | null;
  hobDetailData: DashboardPipelineModel | null;
  osmOverviewData: DashboardPipelineModel | null;
  teamOverviewData: DashboardPipelineModel | null;
  onDepartmentNameClick?: (departmentName: string) => void;
  onHeadOfBusinessNameClick?: (hobName: string) => void;
  onOSMNameClick?: (osmName: string) => void;
  onAgentNameClick?: (agentId: number) => void;
  selectedDate: Date;
}

export const SummaryView = ({
  statistics,
  shouldShowDirectorView,
  shouldShowHoBView,
  shouldShowOSMView,
  shouldShowTeamView,
  selectedHeadOfBusiness,
  selectedOSM,
  selectedDepartmentId,
  hobOverviewData,
  hobDetailData,
  osmOverviewData,
  teamOverviewData,
  onDepartmentNameClick,
  onHeadOfBusinessNameClick,
  onOSMNameClick,
  onAgentNameClick,
  selectedDate,
}: SummaryViewProps) => {
  const { workers } = useAppData();
  const agentNameToIdMap = useMemo(() => {
    const map: Record<string, number> = {};
    workers.forEach((worker) => {
      if (worker.name && worker.userId != null) {
        map[worker.name] = worker.userId;
      }
    });
    return map;
  }, [workers]);

  return (
    <div className="flex flex-grow overflow-hidden">
      <div className="h-full flex-grow overflow-x-hidden overflow-y-auto">
        <TopLevelStatsBox statistics={statistics} selectedDate={selectedDate} />

        <div className="flex flex-col gap-3 p-3 md:p-3">
          {shouldShowDirectorView && hobOverviewData && (
            <DashboardView
              data={hobOverviewData}
              variant="director"
              onNameClick={onHeadOfBusinessNameClick}
              selectedDate={selectedDate}
            />
          )}

          {shouldShowHoBView && selectedHeadOfBusiness && hobDetailData && (
            <DashboardView
              data={hobDetailData}
              variant="hob"
              onNameClick={onOSMNameClick}
              selectedDate={selectedDate}
            />
          )}

          {shouldShowOSMView && selectedOSM && osmOverviewData && (
            <DashboardView
              data={osmOverviewData}
              variant="osm"
              onNameClick={onDepartmentNameClick}
              selectedDate={selectedDate}
            />
          )}

          {shouldShowTeamView && selectedDepartmentId && teamOverviewData && (
            <DashboardView
              data={teamOverviewData}
              variant="team"
              onNameClick={
                onAgentNameClick
                  ? (agentName) => {
                      const agentId = agentNameToIdMap[agentName];
                      if (agentId != null) {
                        onAgentNameClick(agentId);
                      }
                    }
                  : undefined
              }
              selectedDate={selectedDate}
            />
          )}
        </div>
      </div>
    </div>
  );
};
